import { motion } from "framer-motion";
import { MapPin, Clock, Shield, Languages, Phone, Mail } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import EditorialMarquee from "@/components/EditorialMarquee";
import FaqSection from "@/components/FaqSection";
import { BUSINESS } from "@/lib/data";

export default function Contact() {
  const items = [
    {
      icon: Phone,
      label: "Dispatch",
      value: BUSINESS.phone,
      href: BUSINESS.phoneHref,
    },
    {
      icon: Mail,
      label: "Email",
      value: BUSINESS.email,
      href: BUSINESS.emailHref,
    },
    {
      icon: Clock,
      label: "Availability",
      value: "24 / 7 / 365",
    },
    {
      icon: MapPin,
      label: "HQ",
      value: BUSINESS.address,
    },
    {
      icon: Languages,
      label: "Language",
      value: "English / French",
    },
    {
      icon: Shield,
      label: "License",
      value: "NB Class 2 · Insured",
    },
  ];

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact Maze Security — Dispatch & Quote Request | NB"
        description="Reach the duty officer. 24/7 dispatch at (807) 907-2874, info@mazesecurity.ca. Bilingual, insured, deploy in 4 hours anywhere in New Brunswick."
        path="/contact"
      />
      <section className="pt-32 md:pt-40 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Contact · Dispatch ]
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-5xl md:text-9xl leading-[0.9] tracking-tighter"
          >
            Contact Maze Security — <br />
            <i className="text-primary">24/7 NB dispatch.</i>
          </motion.h1>

          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-foreground/80">
            Submit the operations brief below and a licensed New Brunswick
            Security Services duty officer will respond within one business
            hour.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border mt-16">
            {items.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border-r border-b border-border p-8 md:p-10"
              >
                <item.icon size={18} strokeWidth={1.3} className="text-primary mb-6" />
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                  {item.label}
                </div>
                {item.href ? (
                  <a
                    href={item.href}
                    className="font-serif text-xl md:text-2xl leading-tight hover:text-primary transition-colors"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                  >
                    {item.value}
                  </a>
                ) : (
                  <div
                    className="font-serif text-xl md:text-2xl leading-tight"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                  >
                    {item.value}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <QuoteForm />
      <EditorialMarquee />
      <FaqSection />
    </div>
  );
}
